// QR Kiosk v2 — KV2-7 task helpers (pure functions + tokenized maps; NO React).
// TASK_STATUS_META (designer TSTAT → tokens), role helpers, group split, the
// allowedActions render gate (§9.3, extracted so it is unit-testable), and the
// request-body builders (SEC-REQ-B5-4 client half — never a `cost` key).
import { Roles } from "../../../account/models/AccountModels";
import type { ServiceReportFile } from "../../models/serviceReportsModels";
import type { PipTone } from "../shared/StatusPip";
import type {
  KioskTask,
  KioskTaskAction,
  KioskTaskDetail,
  KioskTaskPhoto,
  KioskTaskStatus,
} from "./kioskTasksModels";

// Keyed on the SERVER vocabulary. Unknown value → fallback (defensive, see taskChipMeta).
export const TASK_STATUS_META: Record<KioskTaskStatus, { labelKey: string; tone: PipTone; dashed: boolean }> = {
  todo: { labelKey: "kioskV2TaskTodo", tone: "--tier-accent", dashed: false },
  in_progress: { labelKey: "kioskV2TaskInProgress", tone: "--amber", dashed: false },
  on_hold: { labelKey: "kioskV2TaskOnHold", tone: "--amber", dashed: true },
  done: { labelKey: "kioskV2TaskDone", tone: "--green", dashed: false },
};

export const TASK_DUE_FORMAT = "DD.MM.YYYY"; // due dates: date-only (REPORT_DATE_FORMAT reused for started/closed)

// Chip resolution: known → the tokenized meta; unknown server value → { known:false }
// so the chip renders the raw string in a muted pill (never throws, never hides the row).
export const taskChipMeta = (
  kioskStatus: string
): { known: true; labelKey: string; tone: PipTone; dashed: boolean } | { known: false } => {
  const meta = TASK_STATUS_META[kioskStatus as KioskTaskStatus];
  return meta ? { known: true, ...meta } : { known: false };
};

export const isManagerOrAbove = (role?: string) => role === Roles.ADMIN || role === Roles.MANAGER;

// Copy-key pickers (manager vs worker) — extracted so the picks are unit-testable.
export const taskScopeKey = (role?: string) =>
  isManagerOrAbove(role) ? "kioskV2TasksScopeManager" : "kioskV2TasksScopeWorker";
export const secondGroupEyebrowKey = (role?: string) =>
  isManagerOrAbove(role) ? "kioskV2TasksThisMachine" : "kioskV2TasksUpNext";
export const tasksRowLabelKey = (role?: string) =>
  isManagerOrAbove(role) ? "kioskV2TasksRowManager" : "kioskV2TasksRowWorker";

// Overdue pinned first; server order preserved inside each group (dueDate asc, nulls last).
export const splitTaskGroups = (tasks: KioskTask[]) => ({
  overdue: tasks.filter((t) => t.isOverdue),
  rest: tasks.filter((t) => !t.isOverdue),
});

export const openTaskCount = (tasks: KioskTask[]) => tasks.filter((t) => t.kioskStatus !== "done").length;

export const filesToPhotos = (files: ServiceReportFile[]): KioskTaskPhoto[] =>
  files.map((f) => ({ name: f.name, content: f.content as string }));

// §9.3 candidate buttons per state, intersected with the server's allowedActions.
const STATE_ACTIONS: Record<KioskTaskStatus, KioskTaskAction[]> = {
  todo: ["start"],
  in_progress: ["hold", "done"],
  on_hold: ["resume"],
  done: [],
};

/** The transition buttons that may render = state candidates ∩ allowedActions (the gate). */
export const renderableActions = (detail: KioskTaskDetail): KioskTaskAction[] => {
  const allowed = detail.allowedActions ?? [];
  const candidates = STATE_ACTIONS[detail.kioskStatus] ?? [];
  return candidates.filter((a) => allowed.includes(a));
};

export const canAddNote = (detail: KioskTaskDetail): boolean => (detail.allowedActions ?? []).includes("note");

/** Advisory A2: a non-done task the caller can SEE but not ACT on → honest view-only state. */
export const isViewOnly = (detail: KioskTaskDetail): boolean =>
  detail.kioskStatus !== "done" && (detail.allowedActions?.length ?? 0) === 0;

// ── Request-body builders (SEC-REQ-B5-4 client half) ──
// NEVER add a key the server does not map — the done body rejects any extra property
// (JsonUnmappedMemberHandling.Disallow → hard 400). Empty photo arrays are omitted.
export const buildHoldBody = (reason: string) => ({ reason });

export const buildDoneBody = (
  completionNote: string,
  photos: KioskTaskPhoto[]
): { completionNote: string; photos?: KioskTaskPhoto[] } =>
  photos.length > 0 ? { completionNote, photos } : { completionNote };

export const buildNoteBody = (
  text: string,
  photos: KioskTaskPhoto[]
): { text?: string; photos?: KioskTaskPhoto[] } => {
  const body: { text?: string; photos?: KioskTaskPhoto[] } = {};
  if (text.length > 0) body.text = text;
  if (photos.length > 0) body.photos = photos;
  return body;
};
